import type { ImInvestorDocument } from '@fruitful/types';
import { useCallback, useEffect, useRef } from 'react';

import { requestBuilder } from '../../http/RequestBuilder';

const FIFTY_FIVE_MINUTES_IN_MS = 55 * 60 * 1000;

export const useGetApexAccountDocuments = (accountId: string) => {
    const queryResult = useGetAccountDocuments({
        accountId,
    });

    const lastFetchTimeRef = useRef<number | null>(null);
    const previousAccountIdRef = useRef<string | null>(null);

    // Reset initial fetch time when accountId changes
    useEffect(() => {
        if (
            previousAccountIdRef.current !== null &&
            previousAccountIdRef.current !== accountId
        ) {
            lastFetchTimeRef.current = null;
        }
        previousAccountIdRef.current = accountId;
    }, [accountId]);

    // Track initial fetch time
    if (queryResult.data && lastFetchTimeRef.current === null) {
        lastFetchTimeRef.current = Date.now();
    }

    // Reset if data is cleared
    if (!queryResult.data) {
        lastFetchTimeRef.current = null;
    }

    /**
     * Apex sends signed urls with expiration in 1 hour.
     * Here we refresh the document prior to downloading to ensure we have a valid url.
     * We assume we receive the documents client-side ~immediately after the urls are signed.
     */
    const getDocumentDownloadUrl = useCallback(
        async (document: ImInvestorDocument): Promise<string> => {
            const now = Date.now();
            const initialFetchTime = lastFetchTimeRef.current;

            // Check and refetch if 55 minutes have elapsed since last fetch
            if (
                initialFetchTime !== null &&
                now - initialFetchTime >= FIFTY_FIVE_MINUTES_IN_MS
            ) {
                const refetchResult = await queryResult.refetch();
                lastFetchTimeRef.current = Date.now();

                const updatedDocuments = refetchResult.data;
                if (updatedDocuments) {
                    const allDocuments = [
                        ...(updatedDocuments.statements ?? []),
                        ...(updatedDocuments.tradeConfirmations ?? []),
                        ...(updatedDocuments.taxDocuments ?? []),
                    ];
                    const updatedDocument = allDocuments.find(
                        (doc) => doc.name === document.name,
                    );
                    if (updatedDocument) {
                        return updatedDocument.downloadLink;
                    }
                }
            }

            return document.downloadLink;
        },
        [queryResult],
    );

    return {
        ...queryResult,
        getDocumentDownloadUrl,
    };
};

const useGetAccountDocuments = requestBuilder.query<
    {
        taxDocuments: ImInvestorDocument[];
        statements: ImInvestorDocument[];
        tradeConfirmations: ImInvestorDocument[];
    } | null,
    { accountId: string }
>({
    endpoint: ({ accountId }) =>
        `/api/investment-management/${accountId}/documents`,
    queryKey: ({ accountId }) =>
        `/investment-management/account/${accountId}/documents`,
});
